import { useState } from 'react'
import { query, getDocs, orderBy } from 'firebase/firestore'
import { collection, subCollectionModular } from '@app/firebase/firestore'

/** Hook for managing invoice payment data and related operations */
const useInvoicePayments = () => {
  const [invoicePaymentData, setInvoicePaymentData] = useState([])
  const [invoicePaymentDataLoading, setInvoicePaymentDataLoading] = useState(false)

  /** Fetches invoice payments for a plan admin */
  const fetchInvoicePayments = async ({ planAdminUID }) => {
    if (!planAdminUID) {
      setInvoicePaymentData([])
      return
    }

    setInvoicePaymentDataLoading(true)

    try {
      // Get plan admin document
      const planAdminCollection = collection('planAdmins')
      let planAdminResult

      try {
        const snapshot = await planAdminCollection
          .where('adminIDs', 'array-contains', planAdminUID)
          .limit(1)
          .get()

        planAdminResult = snapshot.empty
          ? null
          : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() }
      } catch (err) {
        console.error('Error fetching plan admin:', err)
        planAdminResult = null
      }

      // Exit early if no admin found
      if (!planAdminResult) {
        setInvoicePaymentData([])
        return
      }

      // Get payments
      const paymentsSnapshot = await getDocs(
        query(
          subCollectionModular({
            docName: 'planAdmins',
            docId: planAdminResult.id,
            subDocName: 'payments',
          }),
          orderBy('invoiceCreatedAt', 'desc'),
        ),
      )

      // Map payments to array
      const payments = paymentsSnapshot.empty
        ? []
        : paymentsSnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id, uid: doc.id }))

      setInvoicePaymentData(payments)
    } catch (error) {
      console.error('Error in fetchInvoicePayments:', error)
      setInvoicePaymentData([])
    } finally {
      setInvoicePaymentDataLoading(false)
    }
  }

  /** Fetches an EMR order by its ID */
  const fetchEmrOrderById = async ({ emrOrderId }) => {
    if (!emrOrderId) return []

    try {
      const doc = await collection('emrOrders').doc(emrOrderId).get()
      return doc.exists ? [{ ...doc.data(), id: doc.id }] : []
    } catch (error) {
      console.error('Error in fetchEmrOrderById:', error)
      return []
    }
  }

  return {
    fetchInvoicePayments,
    invoicePaymentData,
    invoicePaymentDataLoading,
    fetchEmrOrderById,
  }
}

export default useInvoicePayments
